/**
 * The interdiction tug-of-war (plan R6, §2.9 flow 5; spec §4.2; D-TBD 17).
 *
 * The escape vector starts `startOffsetDeg` off the cruise direction and drifts at the pilot profile's rate about an
 * axis (perpendicular to the vector) re-rolled every `driftTurnS`. The `escape` meter gains dt while the nose is
 * within `coneDeg` of the vector and loses `escapeDecay` × dt outside it; the `pull` meter gains dt / `maxS`, times
 * `offVectorPullMult` while off the vector. `escape` ≥ `escapeS` escapes; `pull` ≥ 1 fails.
 *
 * `startTug` and `stepTug` allocate nothing (D-014); the rng makes every tug deterministic for a seed.
 */
import { nextFloat, range, type Rng } from '../../core/rng'
import type { PilotProfile, TravelConfig } from '../config'

const DEG = Math.PI / 180

export type TugResult = 'active' | 'escaped' | 'failed'

export interface TugState {
  profile: PilotProfile
  /** The escape vector (unit, system frame). */
  ex: number
  ey: number
  ez: number
  /** The current drift axis (unit, perpendicular to the vector when rolled). */
  ax: number
  ay: number
  az: number
  /** Seconds until the drift axis is re-rolled. */
  turnLeftS: number
  /** Seconds (net) spent on the vector; escapes at `escapeS`. */
  escape: number
  /** 0..1; fails at 1. */
  pull: number
  /** Whether the nose was inside the cone on the last step. */
  onVector: boolean
  /** Seconds since `startTug`. */
  t: number
}

export function createTug(): TugState {
  return {
    profile: 'rookie',
    ex: 0,
    ey: 0,
    ez: -1,
    ax: 0,
    ay: 1,
    az: 0,
    turnLeftS: 0,
    escape: 0,
    pull: 0,
    onVector: false,
    t: 0,
  }
}

/** Writes into the tug's axis a random unit vector perpendicular to (vx, vy, vz) (a unit vector). */
function rollPerpendicular(tug: TugState, vx: number, vy: number, vz: number, rng: Rng): void {
  // A basis (ux, uy, uz), (wx, wy, wz) of the plane perpendicular to v: u = normalise(v × helper), w = v × u.
  let hx = 0
  let hy = 1
  const hz = 0
  if (vy > 0.9 || vy < -0.9) {
    hx = 1
    hy = 0
  }
  let ux = vy * hz - vz * hy
  let uy = vz * hx - vx * hz
  let uz = vx * hy - vy * hx
  const ul = Math.sqrt(ux * ux + uy * uy + uz * uz)
  ux /= ul
  uy /= ul
  uz /= ul
  const wx = vy * uz - vz * uy
  const wy = vz * ux - vx * uz
  const wz = vx * uy - vy * ux
  const phi = nextFloat(rng) * 2 * Math.PI
  const c = Math.cos(phi)
  const s = Math.sin(phi)
  tug.ax = ux * c + wx * s
  tug.ay = uy * c + wy * s
  tug.az = uz * c + wz * s
}

/** Rotates the tug's escape vector by `angle` about its axis (Rodrigues), then renormalises it. */
function rotateEscape(tug: TugState, angle: number): void {
  const c = Math.cos(angle)
  const s = Math.sin(angle)
  const ax = tug.ax
  const ay = tug.ay
  const az = tug.az
  const ex = tug.ex
  const ey = tug.ey
  const ez = tug.ez
  const kd = (ax * ex + ay * ey + az * ez) * (1 - c)
  let x = ex * c + (ay * ez - az * ey) * s + ax * kd
  let y = ey * c + (az * ex - ax * ez) * s + ay * kd
  let z = ez * c + (ax * ey - ay * ex) * s + az * kd
  const l = Math.sqrt(x * x + y * y + z * z)
  x /= l
  y /= l
  z /= l
  tug.ex = x
  tug.ey = y
  tug.ez = z
}

/** Starts a tug against a cruise heading (any non-zero vector): resets the meters and places the escape vector. */
export function startTug(
  tug: TugState,
  cruiseDirX: number,
  cruiseDirY: number,
  cruiseDirZ: number,
  profile: PilotProfile,
  rng: Rng,
  cfg: TravelConfig,
): void {
  const ic = cfg.interdiction
  const l = Math.sqrt(cruiseDirX * cruiseDirX + cruiseDirY * cruiseDirY + cruiseDirZ * cruiseDirZ)
  const dx = l > 0 ? cruiseDirX / l : 0
  const dy = l > 0 ? cruiseDirY / l : 0
  const dz = l > 0 ? cruiseDirZ / l : -1
  tug.profile = profile
  tug.ex = dx
  tug.ey = dy
  tug.ez = dz
  rollPerpendicular(tug, dx, dy, dz, rng)
  rotateEscape(tug, range(rng, ic.startOffsetDeg[0], ic.startOffsetDeg[1]) * DEG)
  rollPerpendicular(tug, tug.ex, tug.ey, tug.ez, rng)
  tug.turnLeftS = range(rng, ic.driftTurnS[0], ic.driftTurnS[1])
  tug.escape = 0
  tug.pull = 0
  tug.onVector = false
  tug.t = 0
}

/**
 * One tick of the tug with the ship's nose (any non-zero vector). The nose is judged against the vector as it was at
 * the start of the tick; the vector then drifts. Returns 'escaped' or 'failed' when a meter completes.
 */
export function stepTug(
  tug: TugState,
  noseX: number,
  noseY: number,
  noseZ: number,
  dt: number,
  rng: Rng,
  cfg: TravelConfig,
): TugResult {
  const ic = cfg.interdiction
  const nl = Math.sqrt(noseX * noseX + noseY * noseY + noseZ * noseZ)
  const dot = nl > 0 ? (noseX * tug.ex + noseY * tug.ey + noseZ * tug.ez) / nl : -1
  const on = dot >= Math.cos(ic.coneDeg * DEG)
  tug.onVector = on
  if (on) {
    tug.escape += dt
    tug.pull += dt / ic.maxS
  } else {
    tug.escape -= ic.escapeDecay * dt
    if (tug.escape < 0) tug.escape = 0
    tug.pull += (dt / ic.maxS) * ic.offVectorPullMult
  }
  tug.t += dt

  rotateEscape(tug, ic.driftDegPerS[tug.profile] * DEG * dt)
  tug.turnLeftS -= dt
  if (tug.turnLeftS <= 0) {
    rollPerpendicular(tug, tug.ex, tug.ey, tug.ez, rng)
    tug.turnLeftS += range(rng, ic.driftTurnS[0], ic.driftTurnS[1])
    if (tug.turnLeftS <= 0) tug.turnLeftS = ic.driftTurnS[0]
  }

  if (tug.escape >= ic.escapeS) return 'escaped'
  if (tug.pull >= 1) return 'failed'
  return 'active'
}
